import { Component, forwardRef, Input, ChangeDetectionStrategy } from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'
import { TranslateDirective } from '@ngx-translate/core'
import { MatButton } from '@angular/material/button'
import { MatError, MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input'
import { NgxMatDatepickerActions, NgxMatDatepickerApply, NgxMatDatepickerCancel, NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker'
import { AppMatDatepickerToggle } from './datetime-picker-toggle/datetime-picker-toggle.component'
import { ErrorStateMatcher } from '@angular/material/core'

class CustomErrorStateMatcher implements ErrorStateMatcher {
  constructor(private isInvalid: () => boolean) {}

  isErrorState(): boolean {
    return this.isInvalid()
  }
}

@Component({
  selector: 'app-datetime-picker',
  standalone: true,
  imports: [
    FormsModule,
    MatButton,
    MatInput,
    NgxMatDatepickerActions,
    AppMatDatepickerToggle,
    NgxMatDatetimepicker,
    NgxMatDatepickerInput,
    MatSuffix,
    NgxMatDatepickerCancel,
    NgxMatDatepickerApply,
    TranslateDirective,
    MatFormField,
    MatLabel,
    MatError,
  ],
  templateUrl: './datetime-picker.component.html',
  styleUrl: './datetime-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimePickerComponent),
      multi: true,
    },
  ],
})
export class DatetimePickerComponent implements ControlValueAccessor {
  @Input()
  required = true

  @Input()
  translationKey = 'COMMON.DATE'

  @Input()
  isDateInvalid = false

  @Input()
  errorMessageKey = ''

  @Input()
  disabled = false

  value: Date | null = null
  errorStateMatcher: ErrorStateMatcher

  private onChange: (value: Date | null) => void = () => {}
  private onTouched: () => void = () => {}

  constructor() {
    this.errorStateMatcher = new CustomErrorStateMatcher(() => this.isDateInvalid)
  }

  writeValue(value: Date | string | null): void {
    const normalizedValue = this.normalizeValue(value)
    this.value = normalizedValue
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState?(isDisabled: boolean): void {
    // Implement if needed
  }

  onValueChange(newValue: Date | null) {
    this.value = newValue
    this.onChange(newValue)
    this.onTouched()
  }

  private normalizeValue(newValue: Date | string | null) {
    if (newValue === null || newValue === '') {
      return null
    } else if (typeof newValue === 'string') {
      const date = new Date(newValue)
      return !isNaN(date.getTime()) ? date : null
    } else {
      return newValue instanceof Date ? newValue : null
    }
  }
}
